import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES, useGetAllPaginatedData } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { useAlloyFeatureFlag } from './AlloyLaunchDarklyProvider';
import { PROJECT_CLASSIFICATION_TYPES } from './consts';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import { fetchWithAccountType } from './helpers/fetchWithAccountType';
import isProjectTemplate from './helpers/isProjectTemplate';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import useLocalStateData from './hooks/useLocalStateData';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
import {
  EntitlementsProject,
  EntitlementsResponse,
  EntitlementsUser,
} from './projectEntitlementTypes';

type ProjectEntitlementsNoProjectsContextValue = {
  loading: boolean;
  error: false | unknown;
  retry(): void;
  currentProject?: EntitlementsProject;
  user?: EntitlementsUser;
  setUser(user: EntitlementsUser): void;
  setCurrentProject(currentProject: EntitlementsProject): void;
};

export type ProjectEntitlementsContextValue =
  ProjectEntitlementsNoProjectsContextValue & {
    /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
    UNSAFE_projects?: EntitlementsProject[];
    /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
    UNSAFE_addProject?(project: EntitlementsProject): void;
    /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
    UNSAFE_updateProject?(project: EntitlementsProject): void;
    /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
    UNSAFE_removeProjectById?(id: string): void;
  };

/**
 * Context for project entitlements.
 */
export const ProjectEntitlementsContext =
  getOrCreateGlobalContext<ProjectEntitlementsContextValue>(
    CONTEXT_NAMES.ProjectEntitlementsContext,
  );

export const useProjectEntitlements = () =>
  useContextWithValidation(ProjectEntitlementsContext, {
    hook: 'useProjectEntitlements',
    provider: 'ProjectEntitlementsProvider',
  });

/**
 * Hook for getting the current project the user work in.
 * This hook returns the current project, as returned from [project-entitlements](https://platform.stoplight.autodesk.com/docs/hq-api-server/swagger.yaml/paths/~1project-entitlements/get)
 request.
 * Return undefined if project entitlements are missing.
 * @returns {undefined|EntitlementsProject} The project entitlements or undefined
 * @example
 * import { useCurrentProject } from '@adsk/alloy-provider';
 * ...
 * const currentProject = useCurrentProject();
 */
export const useCurrentProject = () => {
  const { currentProject } = useProjectEntitlements();
  return currentProject;
};

/**
 * Hook for getting the current logged in user.
 * This hook returns the current user, as returned from [project-entitlements](https://platform.stoplight.autodesk.com/docs/hq-api-server/swagger.yaml/paths/~1project-entitlements/get)
 request.
 * @returns {{ user: EntitlementsUser, setUser: (user: EntitlementsUser) => void }} An object of shape {user, setUser} - containing the current user and setUser function to update the current user.
 * @example
 * import { useUser } from '@adsk/alloy-provider';
 * ...
 * const { user, setUser } = useUser();
 */
export const useUser = () => {
  const { user, setUser } = useProjectEntitlements();
  return useMemo(() => (user ? { user, setUser } : {}), [user, setUser]);
};

/**
 * This hook returns whether or not the current project is a template.
 * @returns {boolean} Whether the current project entitlements is a template or not.
 * @example
 * import { useCurrentProjectIsTemplate } from '@adsk/alloy-provider';
 * ...
 * const isTemplate = useCurrentProjectIsTemplate();
 */
export const useCurrentProjectIsTemplate = () => {
  const currentProject = useCurrentProject();
  return currentProject ? isProjectTemplate(currentProject) : false;
};

const fields = [
  'accountId',
  'name',
  'accountName',
  'accountImageUrl',
  'defaultUrl',
  'classification',
  'imageUrl',
  'region',
  'products',
  'platform',
];
const extraFieldsForV2 = ['mfaBlocked'];

const classificationFilter = `filter[classification]=${Object.values(
  PROJECT_CLASSIFICATION_TYPES,
).join(',')}`;

const getCurrentProjectIdPart = (projectId: string | undefined) =>
  projectId ? `currentProjectId=${projectId}&` : '';

const noNextPageGetNextKey = () => '';

type ProjectEntitlementsProviderProps = {
  disableAllProjectsFetch?: boolean;
  children?: ReactNode;
};

/**
 * @summary Considerations for Feature Flag Usage within ProjectEntitlementsProvider
 * @description When working with feature flags within providers, follow these guidelines to prevent unexpected issues.
 *
 * @instructions
 * **Context Data Dependencies:**
 *  - Before modifying feature flags within a provider, assess dependencies on context data.
 *  - When feature flag is using targeting rules, it may be dependent on context data, context data isn't available until project-entitlements are fetched.
 *  - Understand potential impacts on provider behavior, especially in interaction with other components.
 */
export const ProjectEntitlementsProvider: FC<
  ProjectEntitlementsProviderProps
> = ({ disableAllProjectsFetch, children }) => {
  const projectId = useCurrentProjectId();

  const [user, setUser] = useState<EntitlementsUser | undefined>(undefined);
  const [currentProject, setCurrentProject] = useState<
    EntitlementsProject | undefined
  >(undefined);
  const [url, setUrl] = useState<string>('');

  const { value: slashClients, loading: slashClientsLoading } =
    useAlloyFeatureFlag('slash-clients');
  const { value: entitlementsV2, loading: entitlementsV2Loading } =
    useAlloyFeatureFlag('entitlements-v2', true);
  const { value: backendSearch, loading: backendSearchLoading } =
    useAlloyFeatureFlag('entitlements-backend-search', disableAllProjectsFetch);
  const disableAllPagesFetch = useMemo(
    () => disableAllProjectsFetch || backendSearch || slashClients,
    [disableAllProjectsFetch, backendSearch, slashClients],
  );
  const isFFLoading = useMemo(
    () => entitlementsV2Loading || backendSearchLoading || slashClientsLoading,
    [entitlementsV2Loading, backendSearchLoading, slashClientsLoading],
  );

  useProviderOnlyOnce({
    context: ProjectEntitlementsContext,
    contextName: CONTEXT_NAMES.ProjectEntitlementsContext,
  });

  useEffect(() => {
    if (url || isFFLoading) {
      return;
    }

    const version = entitlementsV2 ? 'v2' : 'v1';
    const path = slashClients
      ? 'ui/v1/clients'
      : `${version}/project-entitlements`;

    const finalFields = [
      ...fields,
      ...(version === 'v2' ? extraFieldsForV2 : []),
    ].join(',');

    setUrl(
      `${getAdminApiUrl()}/${path}?${getCurrentProjectIdPart(
        projectId,
      )}fields=${finalFields}&${classificationFilter}&limit=${
        disableAllPagesFetch ? 1 : 200
      }`,
    );
  }, [
    entitlementsV2,
    entitlementsV2Loading,
    backendSearchLoading,
    projectId,
    url,
    disableAllPagesFetch,
    slashClientsLoading,
    slashClients,
    isFFLoading,
  ]);

  const {
    data: dataFromBE,
    loading: loadingData,
    error,
    retry,
  } = useGetAllPaginatedData<EntitlementsResponse>({
    url,
    getNextKey:
      isFFLoading || disableAllPagesFetch ? noNextPageGetNextKey : undefined,
    fetcher: fetchWithAccountType,
  });

  useEffect(() => {
    if (error && error.status !== 401) {
      // 401 errors are pretty common and expected, here we want to mostly log unexpected ones
      console.error('Error calling project entitlements', error);
    }
  }, [error]);

  const firstPage = dataFromBE[0];
  useEffect(() => {
    if (firstPage) {
      if (firstPage.currentProject) {
        setCurrentProject(firstPage.currentProject);
      }
      if (firstPage.user) {
        setUser(firstPage.user);
      }
    }
  }, [firstPage]);

  const projectsFromBE = useMemo(() => {
    return (
      dataFromBE &&
      removeOldDuplicatesBy<EntitlementsProject>(
        dataFromBE.flatMap((page) => page.results),
        (item) => item.id,
      )
    );
  }, [dataFromBE]);

  const {
    data: projects,
    addData: addProjects,
    updateEntity: updateProject,
    removeEntityById: removeProjectById,
  } = useLocalStateData<EntitlementsProject>(projectsFromBE);

  const addProject = useCallback(
    (project) => {
      addProjects([project]);
    },
    [addProjects],
  );

  const loading =
    !url || loadingData || Boolean(projectsFromBE.length && !projects.length);

  const value = useMemo(
    () => ({
      loading,
      error,
      retry,
      projects,
      user,
      setUser,
      currentProject,
      setCurrentProject,

      ...(!backendSearch && {
        UNSAFE_projects: projects,
        UNSAFE_addProject: addProject,
        UNSAFE_updateProject: updateProject,
        UNSAFE_removeProjectById: removeProjectById,
      }),
    }),
    [
      loading,
      error,
      retry,
      projects,
      user,
      currentProject,
      backendSearch,
      addProject,
      updateProject,
      removeProjectById,
    ],
  );

  return (
    <ProjectEntitlementsContext.Provider value={value}>
      {children}
    </ProjectEntitlementsContext.Provider>
  );
};

ProjectEntitlementsProvider.displayName = 'ProjectEntitlementsProvider';

ProjectEntitlementsProvider.propTypes = {
  /** Whether to disable all projects fetch when page loads */
  disableAllProjectsFetch: PropTypes.bool,
  /** Nodes with access to context */
  children: PropTypes.node,
};
